import type { VaultProvider } from '../vaults/fetchVaultsAll'

/**
 * `earnUid` — the primary key of the unified earn surface.
 *
 * There is NO single unified format, and deliberately so. There are two forms
 * sharing one grammar (`<venue>:<chainId>:<ref>`):
 *
 * ```
 * lending  →  <Lender>:<chainId>:<ref>              the EXISTING marketUid, verbatim
 * vault    →  vault.<provider>:<chainId>:<address>  the only new form
 * ```
 *
 * Reusing the grammar rather than inventing a format is what buys zero
 * migration on the lending half: a lending row's `earnUid` IS its `marketUid`,
 * so it is already valid on every existing action route.
 *
 * **The uid is OPAQUE.** The meaning of the third segment is venue-dependent —
 * on the lending side it is the underlying for Aave/Morpho/Compound V3, the
 * cToken for Compound V2, the silo for Silo, the eVault for Euler, and an
 * INTEGER marketId for Dolomite (not an address at all). Only `parseEarnUid`
 * may split an `earnUid`; consumers read `EarnMarket.asset.address` to learn
 * what to deposit, never the uid.
 *
 * See EARN_ENDPOINT_PLAN.md §3.
 */

/**
 * Namespace prefix marking the vault form. Vault providers are lowercase and
 * `Lender` keys are uppercase, so bare provider names would already be
 * collision-free — but only by case, which one consumer lowercasing a uid
 * somewhere would silently break. The prefix makes the distinction structural
 * and `isVaultVenue` a `startsWith`.
 */
export const VAULT_VENUE_PREFIX = 'vault.'

/** A lending venue is a `Lender` key; a vault venue is `vault.<provider>`. */
export type EarnVenueKind = 'lending' | 'vault'

export interface ParsedLendingEarnUid {
  kind: 'lending'
  /** The `Lender` key, e.g. `AAVE_V3`. */
  venue: string
  chainId: string
  /**
   * The uid's third segment. Venue-dependent — resolve it with the worker's
   * `parseMarketUid`, never by assuming it is a token address.
   */
  ref: string
  /** Byte-identical to the input. Pass straight to `parseMarketUid`. */
  marketUid: string
}

export interface ParsedVaultEarnUid {
  kind: 'vault'
  /** The full venue string, e.g. `vault.savings`. */
  venue: string
  /** The bare provider, e.g. `savings`. */
  provider: VaultProvider
  chainId: string
  /** Lowercased share-token address (L1 vault address for hypercore). */
  address: string
}

export type ParsedEarnUid = ParsedLendingEarnUid | ParsedVaultEarnUid

/**
 * Cheap syntactic check — does this uid use the vault form? Does not validate
 * the provider; use `parseEarnUid` when correctness matters.
 */
export function isVaultVenue(uidOrVenue: string): boolean {
  return uidOrVenue.startsWith(VAULT_VENUE_PREFIX)
}

/** `savings` → `vault.savings`. */
export function vaultVenue(provider: VaultProvider): string {
  return `${VAULT_VENUE_PREFIX}${provider}`
}

/**
 * Mint the vault form from its parts. The address is lowercased to match the
 * canonical keying used across the vault pipeline (`buildVaultLookup` and the
 * `${chainId}-${address}` DB convention both lowercase).
 *
 * @throws if any part is empty.
 */
export function buildVaultEarnUid(
  provider: VaultProvider,
  chainId: string,
  address: string,
): string {
  if (!provider || !chainId || !address) {
    throw new Error(
      `Invalid earnUid parameters: provider=${provider}, chainId=${chainId}, address=${address}`,
    )
  }
  return `${vaultVenue(provider)}:${chainId}:${address.toLowerCase()}`
}

/**
 * The lending form. This is the IDENTITY FUNCTION over `marketUid` — it exists
 * so call sites read as a deliberate mapping rather than an assignment, and so
 * the shape is validated once at the boundary.
 *
 * Never RECONSTRUCT a lending uid from a row's fields. `/pools/latest` rows
 * carry `marketUid` stamped by the public-data parsers; rebuilding it as
 * `lender:chainId:underlying` is only correct for the default-format lenders
 * and silently mints a wrong key for Compound V2 (needs the cToken) and
 * Dolomite (needs the integer marketId). A wrong uid does not degrade a term
 * sheet — it routes a deposit to the wrong market. See EARN_ENDPOINT_PLAN §3.2.
 *
 * @throws if `marketUid` is not three non-empty colon-separated segments, or
 *         if it collides with the `vault.` namespace.
 */
export function earnUidFromMarketUid(marketUid: string): string {
  const parts = splitUid(marketUid)
  if (!parts) {
    throw new Error(
      `Invalid marketUid: expected 'lender:chainId:ref', got '${marketUid}'`,
    )
  }
  if (isVaultVenue(parts.venue)) {
    throw new Error(
      `marketUid '${marketUid}' uses the reserved '${VAULT_VENUE_PREFIX}' venue namespace`,
    )
  }
  return marketUid
}

/**
 * Split an `earnUid` into its parts, discriminated by form.
 *
 * `knownProviders` defaults to undefined, which accepts any `vault.<x>` venue.
 * Pass the live provider set when parsing untrusted input (an action route's
 * query param) so an unknown provider fails at the edge with a clear message
 * rather than deep inside a dispatcher.
 *
 * @throws on a malformed uid or an unrecognised provider.
 */
export function parseEarnUid(
  earnUid: string,
  knownProviders?: readonly VaultProvider[],
): ParsedEarnUid {
  const parts = splitUid(earnUid)
  if (!parts) {
    throw new Error(
      `Invalid earnUid format: expected '<venue>:<chainId>:<ref>', got '${earnUid}'`,
    )
  }
  const { venue, chainId, ref } = parts

  if (!isVaultVenue(venue)) {
    return {
      kind: 'lending',
      venue,
      chainId,
      // Lower-cased to match `normalizeMarketUid` / `createMarketUid`, which
      // both lowercase the ref segment. Harmless for Dolomite's integer id.
      ref: ref.toLowerCase(),
      marketUid: earnUid,
    }
  }

  const provider = venue.slice(VAULT_VENUE_PREFIX.length)
  if (!provider) {
    throw new Error(`Invalid earnUid: empty vault provider in '${earnUid}'`)
  }
  if (knownProviders && !knownProviders.includes(provider as VaultProvider)) {
    throw new Error(
      `Unknown vault provider '${provider}' in earnUid '${earnUid}'. Expected one of: ${knownProviders.join(',')}`,
    )
  }
  return {
    kind: 'vault',
    venue,
    provider: provider as VaultProvider,
    chainId,
    address: ref.toLowerCase(),
  }
}

/** Non-throwing `parseEarnUid`. Returns undefined instead of throwing. */
export function tryParseEarnUid(
  earnUid: string,
  knownProviders?: readonly VaultProvider[],
): ParsedEarnUid | undefined {
  try {
    return parseEarnUid(earnUid, knownProviders)
  } catch {
    return undefined
  }
}

/** Which half of the surface a uid belongs to, without a full parse. */
export function earnVenueKind(earnUid: string): EarnVenueKind {
  return isVaultVenue(earnUid) ? 'vault' : 'lending'
}

/**
 * Shared splitter. Returns undefined rather than throwing so each caller can
 * frame the error in its own vocabulary (`marketUid` vs `earnUid`).
 *
 * Exactly three segments, all non-empty — the same contract as
 * `normalizeMarketUid`, so a uid this accepts is one the worker's
 * `parseMarketUid` also accepts.
 */
function splitUid(
  uid: string,
): { venue: string; chainId: string; ref: string } | undefined {
  if (!uid) return undefined
  const parts = uid.split(':')
  if (parts.length !== 3) return undefined
  const [venue, chainId, ref] = parts
  if (!venue || !chainId || !ref) return undefined
  return { venue, chainId, ref }
}
